import { useDeleteOrgConnector, useOrgConnectors } from "@/api/connectors";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/utils";
import { OrgConnector } from "@cohesive/api-client";
import { IntegrationIcon, Spinner } from "@cohesive/ui-components";
import { Plus } from "lucide-react";
import React, { useState } from "react";
import toast from "react-hot-toast";
import {
  AUTH_LABELS,
  ConnectorDialog,
  REGISTRATIONS,
} from "./components/connectors/ConnectorDialog";
import {
  chipButtonClassName,
  chipDangerButtonClassName,
  EmptyState,
  LcBadge,
  NESTED_DIALOG_Z,
  PaneBody,
  PaneSection,
  Row,
  smButtonClassName,
} from "./components/manage-pane-primitives";

function describeOrgConnector(connector: OrgConnector): string {
  const registration =
    connector.auth_method === "oauth" &&
    REGISTRATIONS.find((r) => r.value === connector.oauth_registration)?.label;
  return [
    AUTH_LABELS[connector.auth_method],
    registration,
    connector.description,
  ]
    .filter(Boolean)
    .join(" · ");
}

function DeleteOrgConnectorDialog({
  connector,
  onClose,
}: {
  connector: OrgConnector;
  onClose: () => void;
}) {
  const { mutateAsync: remove, isPending } = useDeleteOrgConnector();
  const name = connector.display_name;

  return (
    <Dialog open onOpenChange={() => !isPending && onClose()}>
      <DialogContent
        className={cn("sm:max-w-[425px]", NESTED_DIALOG_Z)}
        overlayClassName={NESTED_DIALOG_Z}
      >
        <DialogHeader>
          <DialogTitle>Delete {name}</DialogTitle>
          <DialogDescription>
            This disconnects every member&apos;s connection to {name} and
            can&apos;t be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" disabled={isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="bg-spectrum-red text-paper hover:bg-[color-mix(in_oklab,var(--spectrum-red)_88%,var(--ink))]"
            disabled={isPending}
            isLoading={isPending}
            onClick={async () => {
              try {
                await remove(connector);
                onClose();
                toast.success(`${name} deleted.`);
              } catch {
                toast.error(`Failed to delete ${name}. Please try again.`);
              }
            }}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Settings › Connectors: the organization's own connectors. */
const OrgConnectors: React.FC = () => {
  const { data: connectors, isLoading, isError } = useOrgConnectors();
  // `null` is the create dialog.
  const [editing, setEditing] = useState<OrgConnector | null>();
  const [deleting, setDeleting] = useState<OrgConnector | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-n-50">
        Could not load connectors. Refresh to try again.
      </div>
    );
  }

  return (
    <PaneBody>
      <div className="flex items-center gap-3 px-2.5">
        <p className="min-w-0 flex-1 text-sm text-n-50">
          MCP servers your organization adds for every member to connect to.
        </p>
        <Button
          size="sm"
          variant="secondary"
          className={cn("shrink-0", smButtonClassName)}
          onClick={() => setEditing(null)}
        >
          <Plus />
          Add connector
        </Button>
      </div>

      {connectors?.length === 0 ? (
        <EmptyState
          title="No connectors yet"
          description="Connectors you add show up for every member under Connectors."
        />
      ) : (
        <PaneSection>
          {connectors?.map((connector) => (
            <Row
              key={connector.id}
              tile={
                <IntegrationIcon
                  imageURL={connector.image_url}
                  platform={connector.slug}
                  className="size-5"
                />
              }
              name={connector.display_name}
              meta={
                <>
                  {/* A phone has no room for the badge beside both verbs. */}
                  {connector.status === "disabled" && (
                    <span className="sm:hidden">Disabled · </span>
                  )}
                  {describeOrgConnector(connector)}
                </>
              }
            >
              {connector.status === "disabled" && (
                <span className="hidden sm:contents">
                  <LcBadge tone="neutral">Disabled</LcBadge>
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                className={chipButtonClassName}
                onClick={() => setEditing(connector)}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={chipDangerButtonClassName}
                onClick={() => setDeleting(connector)}
              >
                Delete
              </Button>
            </Row>
          ))}
        </PaneSection>
      )}

      {editing !== undefined && (
        <ConnectorDialog
          owner="organization"
          connector={editing ?? undefined}
          onClose={() => setEditing(undefined)}
        />
      )}
      {deleting && (
        <DeleteOrgConnectorDialog
          connector={deleting}
          onClose={() => setDeleting(null)}
        />
      )}
    </PaneBody>
  );
};

export default OrgConnectors;
